import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router";
import { formatCount } from "@/features/deployment-dashboard/shared/helpers";
import type { ZonePopulation } from "@/features/deployment-dashboard/types";
import type {
  DashboardAreaRow,
  DashboardSourceState,
  DashboardWidgetStatus,
} from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";

import { DashboardWidgetContentState } from "@/features/deployment-dashboard/widgets/dashboard-widget-content-state";
import { DashboardWidgetAccessState } from "@/features/deployment-dashboard/widgets/dashboard-widget-access-state";
import {
  DashboardWidgetIssueIndicator,
  getDashboardWidgetIssue,
} from "@/features/deployment-dashboard/widgets/dashboard-widget-issue-indicator";
import { ConsoleSurface, SectionHeading } from "@/shared/ui";
import { LocationIcon } from "@/shared/ui/console-icons";

interface ZonePopulationPanelProps {
  areas?: DashboardSourceState<DashboardAreaRow[]>;
  zones?: ZonePopulation[];
  deploymentId?: string;
}

function PopulationValue({
  value,
  unit,
  status,
  message,
  isNotConfigured = false,
}: {
  value: number | null;
  unit: string;
  status?: DashboardWidgetStatus;
  message?: string;
  isNotConfigured?: boolean;
}) {
  const resolvedStatus: Exclude<DashboardWidgetStatus, "ready" | "empty"> =
    status === "ready" || status == null || status === "empty" ? "unavailable" : status;

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 128 }}>
      <Box sx={{ minWidth: 112, textAlign: "right" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
          <Typography
            variant="body1"
            color={value == null ? "text.secondary" : "text.primary"}
            sx={{ fontSize: "1.15rem", fontWeight: 700, lineHeight: 1 }}
          >
            {value == null ? "—" : formatCount(value)}
          </Typography>
          {status != null && status !== "ready" && status !== "empty" && (
            <PopulationStatusHint
              status={resolvedStatus}
              message={message}
              isNotConfigured={isNotConfigured}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          {unit}
        </Typography>
      </Box>
    </Box>
  );
}

function AreaPopulationSummary({
  area,
  populationLabel,
}: {
  area: DashboardAreaRow;
  populationLabel: "total population" | "current population";
}) {
  const population = area.population.value;
  const isNotConfigured = area.population.unavailableReason === "not_configured";
  const message =
    area.population.unavailableReason === "not_configured"
      ? "Total Population is not configured for this area."
      : population == null && area.population.status === "unavailable"
        ? "No recent Total Population data is available for this area."
        : undefined;

  return (
    <PopulationValue
      value={population}
      unit={populationLabel}
      status={area.population.status}
      message={message}
      isNotConfigured={isNotConfigured}
    />
  );
}

function PopulationStatusHint({
  status,
  message,
  isNotConfigured,
}: {
  status: Exclude<DashboardWidgetStatus, "ready" | "empty">;
  message?: string;
  isNotConfigured: boolean;
}) {
  const explanation = message ?? "This population data is currently unavailable.";
  const Icon = isNotConfigured ? WarningAmberOutlinedIcon : InfoOutlinedIcon;

  return (
    <Tooltip title={explanation} arrow>
      <Box
        component="span"
        role={status === "unavailable" || status === "error" ? "alert" : "status"}
        aria-label={explanation}
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          color: isNotConfigured ? theme.palette.warning.light : theme.palette.text.secondary,
          cursor: "help",
        })}
      >
        {status === "loading" ? (
          <CircularProgress size={13} color="inherit" />
        ) : (
          <Icon sx={{ fontSize: "0.82rem" }} />
        )}
      </Box>
    </Tooltip>
  );
}

function DerivedPopulationSummary({ area }: { area: DashboardAreaRow }) {
  if (area.derivedPopulationMetrics?.duty_free_population == null) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 150px 88px",
        alignItems: "center",
        gap: 1.5,
        px: 1.25,
        pl: 2.75,
        py: 0.35,
        minHeight: 36,
        borderLeft: "2px solid",
        borderLeftColor: "divider",
        backgroundColor: alpha("#fff", 0.02),
        "@media (max-width: 720px)": {
          gridTemplateColumns: "minmax(0, 1fr) 128px 76px",
          gap: 1,
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        Duty Free Population
      </Typography>
      <PopulationValue
        value={area.derivedPopulation?.value ?? null}
        unit="people"
        status={area.derivedPopulation?.status}
      />
    </Box>
  );
}

/** Renders API area records joined to population and capacity exclusively by `area_id`. */
export function ZonePopulationPanel({ areas, zones, deploymentId }: ZonePopulationPanelProps) {
  const navigate = useNavigate();
  const legacyRows: DashboardAreaRow[] = (zones ?? []).map((zone) => ({
    id: zone.id,
    name: zone.name,
    population: { status: "ready", value: zone.current },
    capacity: { status: "ready", value: zone.capacity },
    derivedPopulation: { status: "empty", value: null },
  }));
  const areaState: DashboardSourceState<DashboardAreaRow[]> = areas ?? {
    status: legacyRows.length === 0 ? "empty" : "ready",
    data: legacyRows,
  };
  const rows = areaState.data ?? [];
  const populationLabel = areas == null ? "current population" : "total population";
  const areaIssueDetails = {
    unavailableAreaNames: rows
      .filter(
        (area) =>
          area.population.status === "unavailable" &&
          area.population.unavailableReason !== "not_configured"
      )
      .map((area) => area.name),
    notConfiguredAreaNames: rows
      .filter((area) => area.population.unavailableReason === "not_configured")
      .map((area) => area.name),
  };
  const allAreasNotConfigured =
    rows.length > 0 && areaIssueDetails.notConfiguredAreaNames.length === rows.length;
  const areaIssueStatus =
    areaState.status === "unavailable" && allAreasNotConfigured ? "partial" : areaState.status;
  const areaIssue =
    areaIssueStatus === "stale"
      ? undefined
      : getDashboardWidgetIssue(areaIssueStatus, areaIssueDetails);

  return (
    <ConsoleSurface
      component="section"
      sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 1.25 }}
    >
      <SectionHeading
        icon={<LocationIcon sx={{ fontSize: "0.875rem" }} />}
        component="h2"
        action={areaIssue == null ? undefined : <DashboardWidgetIssueIndicator issue={areaIssue} />}
      >
        Population by Area
      </SectionHeading>
      {rows.length === 0 && (areaState.status === "loading" || areaState.status === "empty") ? (
        <DashboardWidgetContentState state={areaState.status} minHeight={160} />
      ) : rows.length === 0 ? (
        <DashboardWidgetAccessState
          status={areaState.status === "ready" ? "empty" : areaState.status}
        />
      ) : (
        <>
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {rows.map((area) => {
              const isLinkable = deploymentId != null;
              const content = (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 150px 88px",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    px: 1.25,
                    py: 0.65,
                    minHeight: 54,
                    "@media (max-width: 720px)": {
                      gridTemplateColumns: "minmax(0, 1fr) 128px 76px",
                      gap: 1,
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {area.name}
                  </Typography>
                  <AreaPopulationSummary area={area} populationLabel={populationLabel} />
                  {isLinkable && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        View live
                      </Typography>
                      <ChevronRightIcon fontSize="small" />
                    </Box>
                  )}
                </Box>
              );

              return (
                <Box
                  key={area.id}
                  component="li"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0.75,
                    overflow: "hidden",
                  }}
                >
                  {isLinkable ? (
                    <ButtonBase
                      onClick={() => navigate(`/deployments/${deploymentId}/live?area=${area.id}`)}
                      aria-label={`Open ${area.name} in live view`}
                      sx={(theme) => ({
                        width: "100%",
                        borderRadius: 0,
                        display: "block",
                        textAlign: "left",
                        "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
                      })}
                    >
                      {content}
                    </ButtonBase>
                  ) : (
                    content
                  )}
                  <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <DerivedPopulationSummary area={area} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </ConsoleSurface>
  );
}
